import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Download, Sparkles, Check, Shuffle, Megaphone, Layers, FileArchive } from "lucide-react";
import { api, BASE, fmtDate } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";

const KIND = { welcome: "Welcome", aotw: "Artist of the Week", aotm: "Artist of the Month", chart: "Chart", battle: "Battle" };

const streamCaption = async (body, onChunk) => {
  const res = await fetch(`${BASE}/api/admin/ig/caption`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("fo_token")}` }, body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("AI caption failed");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  for (;;) { const { done, value } = await reader.read(); if (done) break; onChunk(dec.decode(value, { stream: true })); }
};

const PostCard = ({ p, reload }) => {
  const [cap, setCap] = useState(p.caption);
  const [busy, setBusy] = useState(false);
  const id = `${p.kind}-${p.ref}`;
  const ai = async () => {
    setBusy(true); setCap("");
    try { await streamCaption({ kind: p.kind, ref: p.ref }, (c) => setCap((x) => x + c)); } catch (e) { toast.error(e.message); setCap(p.caption); } finally { setBusy(false); }
  };
  const mark = async (posted) => { await api.post("/admin/ig/posted", { kind: p.kind, ref: p.ref, caption: cap, posted }); toast.success(posted ? "Marked as posted" : "Caption saved"); reload(); };
  return (
    <div className={`panel overflow-hidden ${p.posted_at ? "opacity-60" : ""}`} data-testid={`ig-post-${id}`}>
      <img src={`${BASE}${p.image}`} alt={p.title} loading="lazy" className="aspect-[4/5] w-full bg-[#07090E] object-cover" />
      <div className="space-y-3 p-4">
        <div className="flex items-center justify-between"><span className="label-mono">{KIND[p.kind]}</span>{p.posted_at && <span className="flex items-center gap-1 font-mono text-[10px] uppercase text-[#00F5D4]"><Check className="h-3 w-3" />Posted {fmtDate(p.posted_at)}</span>}</div>
        <div className="font-display font-bold">{p.title}</div>
        <Textarea rows={7} value={cap} onChange={(e) => setCap(e.target.value)} className="border-white/10 bg-[#07090E] text-xs" data-testid={`ig-caption-${id}`} />
        <div className="flex flex-wrap gap-2">
          <a href={`${BASE}${p.image}`} download={`fuorionda-${id}.png`} target="_blank" rel="noreferrer" className="btn-ghost !px-3 !py-1.5 text-xs" data-testid={`ig-download-${id}`}><Download className="h-3.5 w-3.5" />Image</a>
          <button className="btn-ghost !px-3 !py-1.5 text-xs" onClick={() => { navigator.clipboard.writeText(cap); toast.success("Caption copied"); }} data-testid={`ig-copy-${id}`}><Copy className="h-3.5 w-3.5" />Caption</button>
          <button className="btn-ghost !px-3 !py-1.5 text-xs" onClick={ai} disabled={busy} data-testid={`ig-ai-${id}`}><Sparkles className="h-3.5 w-3.5 text-[#00F5D4]" />{busy ? "Writing…" : "AI rewrite"}</button>
          <button className="btn-hot !px-3 !py-1.5 text-xs" onClick={() => mark(!p.posted_at)} data-testid={`ig-mark-${id}`}>{p.posted_at ? "Unmark" : "Mark posted"}</button>
        </div>
      </div>
    </div>
  );
};

const PromoCard = () => {
  const [p, setP] = useState(null);
  const [cap, setCap] = useState("");
  const [busy, setBusy] = useState(false);
  const next = () => api.get("/admin/ig/promo").then((r) => { setP(r.data); setCap(r.data.caption); });
  useEffect(() => { next(); }, []);
  const download = async () => {
    setBusy(true);
    try {
      const blob = await (await fetch(`${BASE}${p.image}`)).blob();
      const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: `fuorionda-${p.ref}.png` });
      a.click(); URL.revokeObjectURL(a.href);
      await navigator.clipboard.writeText(cap).catch(() => {});
      toast.success("Image downloaded, caption copied", { description: "A fresh promo post is ready for next time." });
      await next();
    } catch { toast.error("Download failed"); } finally { setBusy(false); }
  };
  const ai = async () => {
    setBusy(true); const base = cap; setCap("");
    try { await streamCaption({ kind: "promo", ref: p.ref, text: base }, (c) => setCap((x) => x + c)); } catch (e) { toast.error(e.message); setCap(base); } finally { setBusy(false); }
  };
  if (!p) return null;
  return (
    <div className="panel mb-8 grid overflow-hidden md:grid-cols-[minmax(0,360px)_1fr]" data-testid="ig-promo-card">
      <img src={`${BASE}${p.image}`} alt={p.title} className="aspect-[4/5] w-full bg-[#07090E] object-cover" data-testid="ig-promo-image" />
      <div className="space-y-3 p-5">
        <span className="label-mono flex items-center gap-2"><Megaphone className="h-3.5 w-3.5" />Promote Fuorionda · new every download</span>
        <div className="font-display text-lg font-bold" data-testid="ig-promo-title">{p.title}</div>
        <Textarea rows={11} value={cap} onChange={(e) => setCap(e.target.value)} className="border-white/10 bg-[#07090E] text-xs" data-testid="ig-promo-caption" />
        <div className="flex flex-wrap gap-2">
          <button className="btn-hot !px-4 !py-2 text-xs" onClick={download} disabled={busy} data-testid="ig-promo-download"><Download className="h-3.5 w-3.5" />Download + copy caption</button>
          <button className="btn-ghost !px-3 !py-1.5 text-xs" onClick={() => { navigator.clipboard.writeText(cap); toast.success("Caption copied"); }} data-testid="ig-promo-copy"><Copy className="h-3.5 w-3.5" />Caption</button>
          <button className="btn-ghost !px-3 !py-1.5 text-xs" onClick={next} disabled={busy} data-testid="ig-promo-shuffle"><Shuffle className="h-3.5 w-3.5" />Another one</button>
          <button className="btn-ghost !px-3 !py-1.5 text-xs" onClick={ai} disabled={busy} data-testid="ig-promo-ai"><Sparkles className="h-3.5 w-3.5 text-[#00F5D4]" />AI rewrite</button>
        </div>
        <p className="text-xs text-slate-500">12 themes (Founding spots, Blind Drop, charts, plans, open call…) with rotating layouts, colors, hooks and hashtags. The last 6 themes are never repeated.</p>
      </div>
    </div>
  );
};

const saveBlob = (blob, name) => {
  const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: name });
  a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 1000);
};

const CarouselCard = () => {
  const [c, setC] = useState(null);
  const [cap, setCap] = useState("");
  const [busy, setBusy] = useState(false);
  const next = () => api.get("/admin/ig/carousel").then((r) => { setC(r.data); setCap(r.data.caption); });
  useEffect(() => { next(); }, []);
  const done = async () => { toast.success("Slides saved, caption copied", { description: "A new carousel is ready for next time." }); await next(); };
  const save = async () => {
    setBusy(true);
    try {
      await navigator.clipboard.writeText(cap).catch(() => {});
      const files = await Promise.all(c.slides.map(async (u, i) => new File([await (await fetch(`${BASE}${u}`)).blob()], `fuorionda-${c.ref}-${i + 1}.png`, { type: "image/png" })));
      if (navigator.canShare?.({ files })) await navigator.share({ files });
      else for (const f of files) { saveBlob(f, f.name); await new Promise((r) => setTimeout(r, 300)); }
      await done();
    } catch (e) { if (e?.name !== "AbortError") toast.error("Download failed"); } finally { setBusy(false); }
  };
  const zip = async () => {
    setBusy(true);
    try { await navigator.clipboard.writeText(cap).catch(() => {}); saveBlob(await (await fetch(`${BASE}${c.zip}`)).blob(), `fuorionda-${c.ref}.zip`); await done(); }
    catch { toast.error("Download failed"); } finally { setBusy(false); }
  };
  const ai = async () => {
    setBusy(true); const base = cap; setCap("");
    try { await streamCaption({ kind: "carousel", ref: c.ref, text: base }, (x) => setCap((y) => y + x)); } catch (e) { toast.error(e.message); setCap(base); } finally { setBusy(false); }
  };
  if (!c) return null;
  return (
    <div className="panel mb-8 overflow-hidden" data-testid="ig-carousel-card">
      <div className="flex snap-x snap-mandatory gap-2 overflow-x-auto bg-[#07090E] p-2" data-testid="ig-carousel-slides">
        {c.slides.map((u, i) => <img key={u} src={`${BASE}${u}`} alt={`Slide ${i + 1}`} loading="lazy" className="aspect-[4/5] w-[70%] shrink-0 snap-start rounded-lg object-cover sm:w-56" data-testid={`ig-carousel-slide-${i}`} />)}
      </div>
      <div className="space-y-3 p-5">
        <span className="label-mono flex items-center gap-2"><Layers className="h-3.5 w-3.5" />Carousel · {c.slides.length} slides · new every download</span>
        <div className="font-display text-lg font-bold" data-testid="ig-carousel-title">{c.title}</div>
        <Textarea rows={9} value={cap} onChange={(e) => setCap(e.target.value)} className="border-white/10 bg-[#07090E] text-xs" data-testid="ig-carousel-caption" />
        <div className="flex flex-wrap gap-2">
          <button className="btn-hot !px-4 !py-2 text-xs" onClick={save} disabled={busy} data-testid="ig-carousel-save"><Download className="h-3.5 w-3.5" />Save slides + copy caption</button>
          <button className="btn-ghost !px-3 !py-1.5 text-xs" onClick={zip} disabled={busy} data-testid="ig-carousel-zip"><FileArchive className="h-3.5 w-3.5" />ZIP</button>
          <button className="btn-ghost !px-3 !py-1.5 text-xs" onClick={() => { navigator.clipboard.writeText(cap); toast.success("Caption copied"); }} data-testid="ig-carousel-copy"><Copy className="h-3.5 w-3.5" />Caption</button>
          <button className="btn-ghost !px-3 !py-1.5 text-xs" onClick={next} disabled={busy} data-testid="ig-carousel-shuffle"><Shuffle className="h-3.5 w-3.5" />Another one</button>
          <button className="btn-ghost !px-3 !py-1.5 text-xs" onClick={ai} disabled={busy} data-testid="ig-carousel-ai"><Sparkles className="h-3.5 w-3.5 text-[#00F5D4]" />AI rewrite</button>
        </div>
        <p className="text-xs text-slate-500">7 carousels: How to get on air, Blind Drop, Plans, How Flames work, Reports, "Is it pay-to-play?", Founding Artists. On your phone "Save slides" opens the share sheet: choose Save images or Instagram directly.</p>
      </div>
    </div>
  );
};

export const InstagramAdmin = () => {
  const [list, setList] = useState(null);
  const [filter, setFilter] = useState("todo");
  const load = () => api.get("/admin/ig").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  if (!list) return <p className="font-mono text-slate-500">Preparing posts…</p>;
  const shown = list.filter((p) => (filter === "todo" ? !p.posted_at : filter === "done" ? p.posted_at : true));
  return (
    <div data-testid="admin-instagram">
      <p className="mb-4 max-w-3xl text-sm text-slate-400">Ready-to-post Instagram content (1080×1350) generated from live data: welcome posts for every approved artist, weekly charts, Artist of the Week (Blind #1), Artist of the Month and Battles. Download the image, copy the caption (artists are tagged with their Instagram handle) and post from your phone.</p>
      <PromoCard />
      <CarouselCard />
      <div className="mb-6 flex gap-2">
        {[["todo", "To post"], ["done", "Posted"], ["all", "All"]].map(([k, l]) => <button key={k} onClick={() => setFilter(k)} data-testid={`ig-filter-${k}`} className={`rounded-full border px-4 py-1.5 font-mono text-[11px] uppercase ${filter === k ? "border-[#FF2E63] bg-[#FF2E63]" : "border-white/15 text-slate-400"}`}>{l}</button>)}
      </div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{shown.map((p) => <PostCard key={`${p.kind}-${p.ref}-${p.posted_at}`} p={p} reload={load} />)}</div>
      {!shown.length && <p className="py-10 text-center text-slate-500">Nothing here.</p>}
    </div>
  );
};
